import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import profileImg from '../assets/images/Subject.webp';
import '../styles/About.css';

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const textRef = useRef(null);

  // Wrap each word in span for hover effect
  useEffect(() => {
    const paragraphs = sectionRef.current.querySelectorAll('.about-content p');

    paragraphs.forEach(p => {
      const text = p.textContent;
      const words = text.split(' ');
      p.innerHTML = words.map(word => `<span>${word}</span>`).join(' ');
    });
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 75%',
          end: 'top 30%',
          toggleActions: 'play none none reverse',
        }
      });

      tl.from(imageRef.current, {
        x: -80,
        scale: 0.9,
        opacity: 0,
        duration: 0.9,
        ease: 'power3.out'
      })
        .from(textRef.current, {
          x: 80,
          scale: 0.95,
          opacity: 0,
          duration: 0.9,
          ease: 'power3.out'
        }, '-=0.6')
        .from(
          sectionRef.current.querySelectorAll('.stat'),
          {
            y: 40,
            opacity: 0,
            scale: 0.9,
            stagger: 0.15,
            duration: 0.6,
            ease: 'power3.out'
          },
          '-=0.4'
        );
    }, sectionRef);

    return () => ctx.revert(); // ✅ only cleans THIS component
  }, []);


  // Refresh ScrollTrigger after images load
  useEffect(() => {
    const img = new Image();
    img.onload = () => ScrollTrigger.refresh();
    img.src = profileImg;
  }, []);

  return (
    <section ref={sectionRef} className="about" id="about">
      <div className="about-container">
        <div ref={imageRef} className="about-image">
          <div className="image-wrapper">
            <img src={profileImg} alt="Profile" className="profile-img" />
            <div className="image-border"></div>
          </div>
        </div>

        <div ref={textRef} className="about-text">
          <h2 className="about-title">About Me</h2>
          <div className="about-content">
            <p>
              I'm a Full-Stack Developer who loves building applications that are reliable, scalable, and easy to use. I focus a lot on backend development but also keep up with modern frontend tools, so I can deliver complete solutions—from designing databases to polishing the user interface.
            </p>
            <p>
              I've worked on web apps, ERP systems, APIs, and integrations, using frameworks like Frappe/ERPNext, Django, Django REST Framework, React, MySQL, MongoDB, and Docker. I enjoy tackling complex problems, designing clean architectures, and turning real business needs into software that actually works.
            </p>
            <p>
              I thrive in Agile teams, collaborating with others, and always finding ways to make code better and more efficient. I'm also constantly learning—whether it's optimizing performance, improving security, or exploring new tools that make development smoother.
            </p>
            <p>
              Outside of coding, I'm disciplined about fitness and personal growth, and I try to bring that same consistency and focus into everything I do.
            </p>
          </div>
          <div className="about-stats">
            <div className="stat">
              <span className="stat-number">2+</span>
              <span className="stat-label">Years Experience</span>
            </div>
            <div className="stat">
              <span className="stat-number">10+</span>
              <span className="stat-label">Projects Completed</span>
            </div>
            <div className="stat">
              <span className="stat-number">30+</span>
              <span className="stat-label">Happy Clients</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
